import {
  FileDoneOutlined,
  FileSearchOutlined,
  FormOutlined,
  HistoryOutlined,
  LinkOutlined,
  PlusOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  DrawerActionsProvider,
  DrawerCloseButton,
  OrderApprovalHistory,
  OrderChangeShared,
  OrderInspectionTable,
  OrderNotes,
  OrderWorkLogs,
  RelatedOrders,
  useDrawerActionsSlot,
} from '@components';
import { useView } from '@hooks';
import { OrderStatus } from '@models';
import { Order, Vehicle } from '@services';
import { useStore } from '@stores';
import { Badge, Button, Drawer, Space, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import './OrderToolbar.css';

type Panel = 'notes' | 'estimates' | 'inspections' | 'related' | 'history' | 'worklog';

interface IProps {
  order: Order;
  vehicle: Vehicle | undefined;
  /** Opens the service picker; absent when the job's services are locked (estimate out, invoiced). */
  onAddWork?: () => void;
  /**
   * Why work cannot be added right now (estimate out, invoiced). Passed alongside an absent
   * onAddWork: the button stays in the row, disabled, with this as its tooltip.
   */
  addWorkLockedReason?: string;
  /** A place for the services block to draw its Print button, kept beside Add work. */
  printsSlot?: React.ReactNode;
}

/**
 * Everything about the order that is not the job itself, one drawer per button. Replaces the
 * tab strip: the services are the page, the rest is a click away.
 */
export const OrderToolbar: React.FC<IProps> = observer(({ order, vehicle, onAddWork, addWorkLockedReason, printsSlot }) => {
  const { isMobile } = useView();
  const { orderStore, inspectionStore } = useStore();
  const [panel, setPanel] = useState<Panel | null>(null);
  // Content puts its primary action (e.g. "Log time") in the header through this slot.
  const { el: actionsEl, slot: actionsSlot } = useDrawerActionsSlot();

  // A pending estimate locks the save footer, so it must be visible from the toolbar.
  const estimatePending = order.status === OrderStatus.Approval;

  // Counts come with the order. While a drawer is open and has finished loading, its own store is
  // the truth (it can go to zero); on close the figure is written back so the badge does not go
  // stale until the next fetch. Related work is always loaded - the invoice card keeps it so.
  const live = (key: Panel, loading: boolean, value: number, fallback: number) =>
    panel === key && !loading ? value : fallback;
  const noteCount = live('notes', orderStore.orderNotesLoading, orderStore.orderNotesTotalCount, order.noteCount);
  const inspectionCount = live('inspections', inspectionStore.loading, inspectionStore.totalCount, order.inspectionCount);
  const workLogCount = live('worklog', orderStore.orderWorkLogsLoading, orderStore.orderWorkLogs.size, order.workLogCount);
  const estimateCount = live('estimates', orderStore.approvalHistoryLoading, orderStore.approvalHistoryTotalCount, order.estimateCount);
  // Loaded by the invoice card when there is one (managers) or by the drawer itself otherwise -
  // and only trusted when it was loaded for this order, never a previous drawer's.
  const relatedLoaded = orderStore.relatedOrdersOrderId === order.id && !orderStore.relatedLoading;
  const relatedOrderCount = relatedLoaded ? orderStore.relatedOrders.size : order.relatedOrderCount;

  const closePanel = () => {
    if (panel === 'notes') orderStore.updateOrderField({ noteCount });
    if (panel === 'inspections') orderStore.updateOrderField({ inspectionCount });
    if (panel === 'worklog') orderStore.updateOrderField({ workLogCount });
    if (panel === 'related') orderStore.updateOrderField({ relatedOrderCount });
    if (panel === 'estimates') orderStore.updateOrderField({ estimateCount });
    setPanel(null);
  };

  // Informational counts in the app blue; red is kept for the one that needs acting on.
  const COUNT_BLUE = '#2CBDFF';
  const counted = (count: number, node: React.ReactNode, attention = false) => (
    <Badge count={count} size="small" offset={[-4, 4]} color={attention ? undefined : COUNT_BLUE}>
      {node}
    </Badge>
  );

  const panels: Record<Panel, { title: string; content: React.ReactNode; wide?: boolean }> = {
    // Notes carries the request concerns, documents and the note feed side by side.
    notes: { title: 'Notes', content: <OrderNotes order={order} />, wide: true },
    estimates: {
      title: 'Estimates',
      content: (
        <OrderApprovalHistory
          orderId={order.id}
          customerEmail={order.customer?.email}
          customerId={order.customer?.id}
          customerUserId={order.customer?.userId}
          customerFullName={order.customer?.fullname}
          orderStatus={order.status}
        />
      ),
      wide: true,
    },
    inspections: { title: 'Inspections', content: <OrderInspectionTable orderId={order.id} /> },
    related: { title: 'Related work', content: <RelatedOrders order={order} vehicle={vehicle} /> },
    history: { title: 'History logs', content: <OrderChangeShared order={order} /> },
    // Six columns - technician, service, when, duration, note, actions - do not fit 720px without
    // wrapping the buttons; it takes the same width as Notes.
    worklog: { title: 'Work logs', content: <OrderWorkLogs order={order} />, wide: true },
  };

  const button = (key: Panel, label: string, icon: React.ReactNode) => (
    <Button key={key} icon={icon} onClick={() => setPanel(key)}>
      {label}
    </Button>
  );

  return (
    <>
      <div className={`order-toolbar${isMobile ? ' order-toolbar-mobile' : ''}`}>
        {counted(relatedOrderCount, button('related', 'Related work', <LinkOutlined />))}
        {counted(workLogCount, button('worklog', 'Work logs', <TeamOutlined />))}
        {counted(inspectionCount, button('inspections', 'Inspections', <FileSearchOutlined />))}
        {counted(
          estimateCount,
          button('estimates', 'Estimates', <FileDoneOutlined />),
          estimatePending
        )}
        {counted(noteCount, button('notes', 'Notes', <FormOutlined />))}
        {button('history', 'History logs', <HistoryOutlined />)}
        {/* Print comes from the services block below; it is an order-wide action, so it sits
            with Add work rather than at the end of one service's row of part actions. */}
        <span className="order-toolbar-prints">{printsSlot}</span>
        {/* Hard right, away from the rest: everything to its left opens something to read, this
            one changes the job. Locked (estimate out, invoiced) it stays put, disabled - hidden,
            it only looked as though the toolbar had lost a button. */}
        {(onAddWork || addWorkLockedReason) && (
          <Tooltip title={addWorkLockedReason}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="order-toolbar-primary"
              disabled={!onAddWork}
              onClick={onAddWork}
            >
              Add work
            </Button>
          </Tooltip>
        )}
      </div>

      <Drawer
        title={panel ? panels[panel].title : ''}
        open={panel !== null}
        onClose={closePanel}
        styles={{
          wrapper: { width: isMobile ? '100%' : panel && panels[panel].wide ? 'min(1100px, 90vw)' : 720 },
        }}
        destroyOnHidden
        closable={false}
        extra={
          <Space>
            {actionsSlot}
            <DrawerCloseButton onClick={closePanel} />
          </Space>
        }
      >
        <DrawerActionsProvider value={actionsEl}>{panel && panels[panel].content}</DrawerActionsProvider>
      </Drawer>
    </>
  );
});
